import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  TextField,
  Autocomplete,
  InputAdornment,
  Typography,
  Divider,
  Popper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { useGlobalSearch, SearchResult } from "../../Dashboard/TopBar/useGlobalSearch";
import {
  topbarSearchInput,
  pageSearchInput,
  subtleHoverEffect,
} from "../../../theme";
import { getDisplayCurrencySymbol } from "../../../utils/stockPriceFormatter";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { isNative } from "../../../lib/platform";
import { hapticSelection } from "../../../lib/haptics";

// Helper function to format market cap with currency-aware symbol
const formatMarketCap = (marketCap: number, currency?: string): string => {
  const symbol = getDisplayCurrencySymbol(currency);
  if (marketCap >= 1e12) {
    return `${symbol}${(marketCap / 1e12).toFixed(1)}T`;
  } else if (marketCap >= 1e9) {
    return `${symbol}${(marketCap / 1e9).toFixed(1)}B`;
  } else if (marketCap >= 1e6) {
    return `${symbol}${(marketCap / 1e6).toFixed(0)}M`;
  }
  return `${symbol}${marketCap.toLocaleString()}`;
};

interface SearchBarProps {
  placeholder?: string;
  width?: number | string;
  variant?: "topbar" | "page" | "chatgpt" | "compact";
  mode?: "charts" | "earnings";
  onSelectItem?: (item: SearchResult) => void;
  disabled?: boolean;
  /** Input height in px (chatgpt/compact variants). Defaults to the chat
      composer's 52px on web (44 on native) so hero search ≡ hero composer;
      pass a smaller value in dense contexts (e.g. ViewTicker). */
  height?: number;
}

const SearchBar = ({
  placeholder = "Search stocks, companies...",
  width = 500,
  variant = "page",
  mode = "charts",
  onSelectItem,
  disabled = false,
  height,
}: SearchBarProps) => {
  const navigate = useNavigate();
  const { trackFeature } = useAnalytics();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const { data: searchResults = [], isLoading: isSearching } =
    useGlobalSearch(search, { enabled: !disabled });

  const isTopbarVariant = variant === "topbar";
  const isChatGPTVariant = variant === "chatgpt";
  const isCompactVariant = variant === "compact";

  // Web mirrors the AI chat composer (16px radius, solid interior, 1rem type,
  // 52px height) so search and chat read as the same input family. Native
  // keeps the flat 12px radius of its rows/cards and a size that fits phones.
  const chatGptFontSize = isNative() ? "0.9375rem" : "1rem";
  const chatGptPaddingX = isNative() ? "16px" : "18px";
  const chatGptRadius = isNative() ? "12px" : "16px";
  const chatGptDropdownRadius = isNative() ? "12px" : "16px";
  const chatGptHeight = height ?? (isNative() ? 44 : 52);

  // Control when dropdown should be open
  React.useEffect(() => {
    setIsOpen(search.length > 0 && searchResults.length > 0);
  }, [search, searchResults]);

  // Custom Popper that doesn't render when there's no search text. Full input
  // width — the dropdown aligns edge-to-edge with the field it belongs to.
  const CustomPopper = (props: any) => {
    if (search.length === 0) {
      return null; // Don't render anything when no search text
    }
    return <Popper {...props} />;
  };

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      loading={isSearching}
      open={disabled ? false : isOpen}
      onOpen={() => {
        // Only allow opening if there's search text and results
        if (search.length > 0 && searchResults.length > 0) {
          setIsOpen(true);
        }
      }}
      onClose={() => {
        setIsOpen(false);
      }}
      PopperComponent={isChatGPTVariant ? CustomPopper : Popper} // Use custom Popper for ChatGPT variant
      disableClearable={isChatGPTVariant} // No clear button for ChatGPT variant
      forcePopupIcon={false} // Hide dropdown arrow for all variants
      ListboxProps={{
        style: {
          maxHeight: 'none',
          overflow: 'visible',
        }
      }}
      // The dropdown renders in a portal, so root-level sx can never reach it —
      // every panel style must live here on the paper slot.
      slotProps={{
        paper: {
          sx: {
            display: search.length === 0 ? "none" : "block",
            borderRadius: isChatGPTVariant ? chatGptDropdownRadius : 3,
            // Solid flat panel matching the composer surface.
            background: (theme) => (theme.palette.mode === "dark" ? "#232323" : "#ffffff"),
            backgroundImage: "none",
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.12)"
                : "1px solid rgba(0, 0, 0, 0.12)",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 16px 40px rgba(0, 0, 0, 0.5)"
                : "0 16px 40px rgba(0, 0, 0, 0.12)",
            // Detached floating panel: the gap lets both the bar's and the
            // panel's rounded corners read cleanly instead of colliding.
            mt: 1,
            maxHeight: "none",
            height: "auto",
            overflow: "visible",
            overflowX: "hidden",
            animation: "searchDropIn 0.2s cubic-bezier(0.22, 1, 0.36, 1) backwards",
            "@keyframes searchDropIn": {
              from: { opacity: 0, transform: "translateY(-6px) scale(0.99)" },
              to: { opacity: 1, transform: "none" },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            "& .MuiAutocomplete-listbox": {
              padding: "6px",
              maxHeight: "none",
              overflow: "visible",
              "& .MuiAutocomplete-option": {
                padding: "8px 10px",
                minHeight: "auto",
                overflow: "hidden",
                borderRadius: isChatGPTVariant ? "10px" : 1.5,
                transition: "background-color 0.15s ease",
                // Keyboard focus paints the same highlight as hover — arrow-key
                // navigation was previously invisible.
                "&:hover, &.Mui-focused, &[aria-selected='true'].Mui-focused": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)",
                },
                "&[aria-selected='true']": {
                  backgroundColor: "transparent",
                },
              },
            },
            "& .MuiAutocomplete-noOptions": {
              padding: 3,
              textAlign: "center",
              color: "text.secondary",
              fontSize: "0.875rem",
            },
          },
        },
      }}
      options={searchResults}
      filterOptions={(options) => options}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.name
      }
      onInputChange={(_, newInputValue) => setSearch(newInputValue)}
      onChange={(_, value) => {
        if (value && typeof value !== "string") {
          hapticSelection();
          if (onSelectItem) {
            // Use custom callback if provided
            onSelectItem(value);
          } else {
            // Default navigation behavior
            trackFeature("stock_search", {
              selected_ticker: value.symbol,
              mode,
            });
            if (mode === "earnings") {
              navigate(`/earnings/company/${encodeURIComponent(value.symbol)}`);
            } else {
              navigate(`/charts?ticker=${encodeURIComponent(value.symbol)}`);
            }
          }
        }
      }}
      renderOption={(props, option, { index }) => {
        // React 18.3+ warns when `key` arrives via spread — lift it out.
        const { key: _key, ...optionProps } = props as { key?: string } & React.HTMLAttributes<HTMLLIElement>;
        return (
          <Box
            component="li"
            key={option.symbol}
            {...optionProps}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: "100%",
              cursor: "pointer",
              // Rows cascade in on first render; persisting rows keep their DOM
              // node across keystrokes, so only fresh results re-animate.
              animation: "searchRowIn 0.25s ease backwards",
              animationDelay: `${Math.min(index, 6) * 0.03}s`,
              "@keyframes searchRowIn": {
                from: { opacity: 0, transform: "translateY(4px)" },
                to: { opacity: 1, transform: "none" },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                flexShrink: 0,
                width: 32,
                height: 32,
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)",
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.08)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
              }}
            >
              {option.logoBase64 && (
                <img
                  src={option.logoBase64}
                  alt={`${option.name} logo`}
                  style={{ width: 22, height: 22, objectFit: "contain" }}
                />
              )}
              {!option.logoBase64 && (
                <SearchIcon sx={{ color: "text.secondary", fontSize: 16 }} />
              )}
            </Box>

            {/* Name, then ticker + exchange pill beneath (the house idiom —
                bold ticker text with a neutral exchange badge, as on the
                market-mover cards). */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9375rem",
                  lineHeight: 1.3,
                  color: (theme) => (theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f"),
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {option.symbol}
                </Typography>
                {option.exchange && (
                  <Typography
                    sx={{
                      fontSize: "0.625rem",
                      fontWeight: 600,
                      px: 0.5,
                      py: 0.125,
                      borderRadius: 0.5,
                      color: "text.secondary",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {option.exchange}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Market cap — quiet, aligned figures (green stays semantic) */}
            {option.marketCap && (
              <Box sx={{ flexShrink: 0, textAlign: "right", ml: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    lineHeight: 1.3,
                    fontVariantNumeric: "tabular-nums",
                    color: (theme) => (theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f"),
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatMarketCap(option.marketCap, option.currency)}
                </Typography>
                <Typography
                  sx={{
                    display: { xs: "none", sm: "block" },
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "text.secondary",
                    opacity: 0.7,
                    whiteSpace: "nowrap",
                  }}
                >
                  Mkt cap
                </Typography>
              </Box>
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={placeholder}
          size="small"
          InputProps={{
            ...params.InputProps,
            // Hero variant carries no leading glyph — like the chat composer,
            // the field itself is the affordance and the text starts flush.
            startAdornment: isChatGPTVariant ? undefined : (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    color: isTopbarVariant
                      ? "rgba(255,255,255,0.7)"
                      : "text.secondary",
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isSearching && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={
            isChatGPTVariant
              ? {
                  // Mirror of the AI chat composer: solid interior, neutral
                  // hairline, greens only on focus, border-colour transition
                  // only (flat UI — no glows).
                  "& .MuiOutlinedInput-root": {
                    fontSize: chatGptFontSize,
                    // Height is explicit (not padding-derived) so it can match
                    // the chat composer exactly and be overridden per context.
                    minHeight: chatGptHeight,
                    padding: `0 ${chatGptPaddingX}`,
                    alignItems: "center",
                    borderRadius: chatGptRadius,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#232323" : "#f7f7f7",
                    border: (theme) =>
                      theme.palette.mode === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.12)"
                        : "1px solid rgba(0, 0, 0, 0.12)",
                    transition: "border-color 0.2s ease",
                    // The interior NEVER shifts — the global MuiTextField theme
                    // override paints a lighter grey on hover/focus, so each
                    // state must re-pin the composer's solid surface.
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "#232323" : "#f7f7f7",
                      borderColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(0, 0, 0, 0.2)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "#232323" : "#f7f7f7",
                      borderColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(34, 197, 94, 0.5)"
                          : "rgba(34, 197, 94, 0.45)",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& input": {
                      padding: 0,
                      fontSize: chatGptFontSize,
                      fontWeight: 400,
                      "&::placeholder": {
                        opacity: 0.6,
                      },
                    },
                  },
                }
              : isCompactVariant
              ? {
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    ...(height !== undefined && { minHeight: height, alignItems: "center" }),
                    padding: height !== undefined ? "0 12px" : "6px 12px",
                    borderRadius: "12px", // Smaller border radius
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.02)",
                    border: (theme) =>
                      theme.palette.mode === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.1)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.03)",
                      borderColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(0, 0, 0, 0.2)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.03)",
                      borderColor: "#22c55e",
                      boxShadow: "0 0 0 2px rgba(34, 197, 94, 0.1)",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& input": {
                      padding: 0,
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      "&::placeholder": {
                        opacity: 0.6,
                      },
                    },
                  },
                }
              : isTopbarVariant
              ? topbarSearchInput
              : pageSearchInput
          }
        />
      )}
      sx={{
        width: width,
        maxWidth: "100%",
        "& .MuiInputBase-root": {
          color: isTopbarVariant ? "white" : "inherit",
        },
        "& .MuiAutocomplete-popupIndicator": {
          color: isTopbarVariant ? "rgba(255,255,255,0.7)" : "text.secondary",
        },
        "& .MuiAutocomplete-clearIndicator": {
          color: isTopbarVariant ? "rgba(255,255,255,0.7)" : "text.secondary",
        },
        // Icon variants: visually equal space each side of the glyph. The
        // doubled ampersand out-ranks MUI's own size-small input padding; the
        // text inset is smaller than the field padding because the glyph's SVG
        // whitespace sits mostly on its right.
        ...(!isChatGPTVariant && {
          "& .MuiOutlinedInput-root": { paddingLeft: "12px" },
          "& .MuiInputAdornment-positionStart": { marginRight: 0 },
          "&& .MuiOutlinedInput-root .MuiAutocomplete-input": { paddingLeft: "6px" },
        }),
      }}
    />
  );
};

export default SearchBar;
